// Meta's WhatsApp Flows limits (Flow JSON 7.3) that the builder and the server enforce, plus the
// fork's limits for interactive flow messages. Character counts are in Unicode code points.
// Sources: Meta's Flows components / Flow JSON references (see DESIGN.md); values Meta does not
// document are marked "ours".

export const FLOW_JSON_VERSION = '7.3';

/** Meta's Flow endpoint protocol version (`data_api_version`); 3.0 is the only current one. */
export const DATA_API_VERSION = '3.0';

export const LIMITS = {
  /** Ours: the form's display name (the Meta flow name adds " · w3send"). */
  formName: 80,
  /** Ours: hard cap; Meta documents no screen limit but advises short flows. */
  screensMax: 20,
  /** The builder warns beyond this many screens. */
  screensWarnAbove: 10,
  /** Ours: Meta documents no limit for the top-bar title; longer ones are cut off on phones. */
  screenTitle: 30,
  /** Meta: 50 components per screen, the Footer included. */
  componentsPerScreen: 49,
  footerLabel: 35,
  heading: 80,
  subheading: 80,
  body: 4096,
  helperText: 80,
  textInputLabel: 20,
  textAreaLabel: 20,
  /** Meta's default max-chars for TextInput when none is set. */
  textInputDefaultMaxChars: 80,
  /** Ours: an upper bound for min/max characters (Meta documents defaults, not maxima). */
  textMaxChars: 4096,
  /** Meta's default max-length for TextArea. */
  textAreaDefaultMaxLength: 600,
  dropdownLabel: 20,
  choiceLabel: 30,
  choiceDescription: 300,
  optionTitle: 30,
  optionDescription: 300,
  dropdownOptions: { min: 1, max: 200 },
  radioOptions: { min: 1, max: 20 },
  checkboxOptions: { min: 1, max: 20 },
  dateLabel: 40,
  optInLabel: 120,
  optInsPerScreen: 5,
  imagesPerScreen: 3,
  /** Meta: images should be ≤ 300 KB (base64 JPEG or PNG). */
  imageBytes: 300 * 1024,
  /** Ours: all images of a form together, so the Flow JSON fits the Graph proxy's 5 MB upload limit. */
  imageBytesPerForm: 2 * 1024 * 1024,
  imageAltText: 100,
  imageHeight: { min: 40, max: 400, default: 160 },
  /** Chatwoot label titles. */
  labelTitle: 255,
  /** Chatwoot custom attribute keys. */
  attributeKey: 60,
  /** Meta's default selectable DatePicker range. */
  dateRange: { min: '1900-01-01', max: '2100-12-31' },
  /** The fork's InteractivePayload: flow_cta ≤ 30 (Meta advises ≤ 30), body ≤ 1024, header/footer ≤ 60. */
  flowCta: 30,
  messageBody: 1024,
  messageHeader: 60,
  messageFooter: 60,
} as const;

export const FLOW_CATEGORIES = [
  'SIGN_UP',
  'SIGN_IN',
  'APPOINTMENT_BOOKING',
  'LEAD_GENERATION',
  'CONTACT_US',
  'CUSTOMER_SUPPORT',
  'SURVEY',
  'OTHER',
] as const;
export type FlowCategory = (typeof FLOW_CATEGORIES)[number];

export const CATEGORY_LABELS: Record<FlowCategory, string> = {
  SIGN_UP: 'Sign up',
  SIGN_IN: 'Sign in',
  APPOINTMENT_BOOKING: 'Appointment booking',
  LEAD_GENERATION: 'Lead generation',
  CONTACT_US: 'Contact us',
  CUSTOMER_SUPPORT: 'Customer support',
  SURVEY: 'Survey',
  OTHER: 'Other',
};

/** Payload keys the generator adds to every `complete` payload; field names may not use them. */
export const RESERVED_PAYLOAD_KEYS = ['flow_token', 'w3send_form', 'w3send_version'] as const;

/** Length in Unicode code points, the way users (and Meta) count characters. */
export const charCount = (value: string): number => Array.from(value).length;
